/**
 * Vehicles Tab Component
 * Main vehicle management view with filters and summary stats
 */

import { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useVehicles } from '../../../hooks/useVehicles';
import { VehicleTable } from './VehicleTable';
import { CreateVehicleModal } from './CreateVehicleModal';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import type { VehicleStatus } from '../../../services/vehicle.service';

export function VehiclesTab() {
  const [statusFilter, setStatusFilter] = useState<VehicleStatus | undefined>(undefined);
  const [maintenanceDueFilter, setMaintenanceDueFilter] = useState<boolean | undefined>(undefined);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data, isLoading, error } = useVehicles({
    status: statusFilter,
    maintenanceDue: maintenanceDueFilter,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Error loading vehicles: {error.message}
      </div>
    );
  }

  const vehicles = data?.vehicles || [];
  const stats = data?.byStatus || {
    available: 0,
    assigned: 0,
    maintenance: 0,
    out_of_service: 0,
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="p-4">
          <div className="text-sm font-medium text-gray-500">Available</div>
          <div className="mt-2 text-2xl font-bold">{stats.available}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium text-gray-500">Assigned</div>
          <div className="mt-2 text-2xl font-bold">{stats.assigned}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium text-gray-500">Maintenance</div>
          <div className="mt-2 text-2xl font-bold">{stats.maintenance}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-medium text-gray-500">Total</div>
          <div className="mt-2 text-2xl font-bold">{data?.total || 0}</div>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value as VehicleStatus || undefined)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All Statuses</option>
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="maintenance">Maintenance</option>
            <option value="out_of_service">Out of Service</option>
          </select>

          <select
            value={maintenanceDueFilter === undefined ? '' : String(maintenanceDueFilter)}
            onChange={(e) =>
              setMaintenanceDueFilter(e.target.value === '' ? undefined : e.target.value === 'true')
            }
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All Vehicles</option>
            <option value="true">Maintenance Due</option>
            <option value="false">No Maintenance Due</option>
          </select>

          {(statusFilter || maintenanceDueFilter !== undefined) && (
            <button
              onClick={() => {
                setStatusFilter(undefined);
                setMaintenanceDueFilter(undefined);
              }}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Clear Filters
            </button>
          )}
        </div>

        <Button onClick={() => setIsCreateModalOpen(true)} className="whitespace-nowrap">
          <Plus className="mr-2 h-4 w-4" />
          Add Vehicle
        </Button>
      </div>

      {/* Vehicle Table */}
      {vehicles.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-gray-500">
            {statusFilter || maintenanceDueFilter !== undefined
              ? 'No vehicles match the selected filters'
              : 'No vehicles yet. Add your first vehicle to get started.'}
          </div>
          {!statusFilter && maintenanceDueFilter === undefined && (
            <Button onClick={() => setIsCreateModalOpen(true)} className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Add Vehicle
            </Button>
          )}
        </Card>
      ) : (
        <VehicleTable vehicles={vehicles} />
      )}

      {/* Create Modal */}
      <CreateVehicleModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
}
